/**
 * Superadmin organization DETAIL PAGE — routed at `/platform/organizations/:organizationId`,
 * replacing the inline accordion the list used to expand.
 *
 * The accordion was the third shape this console had for "open a record", and the worst of
 * the three: the detail rendered INSIDE the table, so it had no edge of its own and bled into
 * the row below it (the owner's "inguardabile"). Tenant and Licenza had already been moved to
 * their own routed pages for the same reason — this makes the three entities agree, and gives
 * the detail a shareable URL, a back button, and the full page width.
 *
 * Deliberately NO tabs (owner's call): the three sections run in sequence on one screen.
 * A tab rail is worth its cost when the sections are alternatives; here they are one answer
 * to one question — "what does this company actually have with us?" — and splitting it into
 * three clicks would hide exactly the join the page exists to show.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Archive,
  Building2,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Server,
  Trash2,
  TriangleAlert,
  Users,
} from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ClaimLink } from "@/components/platform/ClaimLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer, PageContent } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { reportSummary, reportVersion } from "@/lib/licenseReport";
import {
  type OrgMemberAdded,
  type PlatformOrganization,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

import { OrganizationDialog } from "./OrganizationDialog";
import { type LinkTarget, OrganizationLinkDialog } from "./OrganizationLinkDialog";

const ORGANIZATIONS_KEY = ["platform", "organizations"];

/**
 * A titled section — a heading and its content, with NO panel around it (owner's call).
 *
 * The page is already one bounded surface, so boxing each block drew a second frame inside
 * the first and turned three lists into three floating cards. The heading + the table's own
 * header band carry the separation; the content below is a GRID, like every other list in
 * this console.
 */
function Section({
  title,
  icon: Icon,
  count,
  action,
  children,
}: {
  title: string;
  icon?: typeof Server;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {count !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
            {count}
          </span>
        )}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/** The empty state of a grid — kept flush with the table's own left edge. */
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>;
}

/** A flat header metric (label over value) — no box, control-panel style. */
function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

/** One label/value pair of the anagrafica. An unset field says "—", never an empty cell. */
function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-foreground">{value?.trim() || "—"}</div>
    </div>
  );
}

/**
 * Add a person to the organization — the same dialog shape the partner and staff hats use.
 *
 * NO password field, on purpose: we never hold a credential on somebody else's behalf. If the
 * address already has an Account Tray we LINK it; if it doesn't we create one password-less
 * and the person chooses. The outcome is a SCREEN and not a toast because it carries the
 * `claim_url` — the link is the guarantee, the mail is the convenience.
 */
function AddOrgMemberDialog({
  organizationId,
  onClose,
  onAdded,
}: {
  organizationId: string;
  onClose: () => void;
  onAdded: (res: OrgMemberAdded) => void;
}) {
  const [email, setEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [role, setRole] = React.useState<"admin" | "member">("admin");
  const [done, setDone] = React.useState<OrgMemberAdded | null>(null);

  const save = useMutation({
    mutationFn: () =>
      platformApi.addOrgMember(organizationId, {
        email: email.trim().toLowerCase(),
        role,
        full_name: fullName.trim() || null,
      }),
    onSuccess: (res) => {
      setDone(res);
      onAdded(res);
    },
  });

  if (done) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {done.mode === "linked" ? "Persona collegata" : "Invito inviato"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-foreground">
              {done.mode === "linked" ? (
                <>
                  <strong>{email.trim().toLowerCase()}</strong> aveva già un Account Tray: ora
                  appartiene anche a questa organizzazione, e accede con le credenziali che ha già.
                </>
              ) : (
                <>
                  Abbiamo creato l&apos;Account Tray di{" "}
                  <strong>{email.trim().toLowerCase()}</strong>. La password la sceglie lei
                  raccogliendo l&apos;invito — noi non ne teniamo una.
                </>
              )}
            </p>
            {!done.mail_sent && done.claim_url && (
              <p className="text-sm text-warning">
                La mail non è partita. Passa tu il link qui sotto: senza, l&apos;invito non arriva a
                nessuno.
              </p>
            )}
            {(done.claim_url || done.login_url) && (
              <ClaimLink url={(done.claim_url ?? done.login_url) as string} />
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Aggiungi una persona</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Se l&apos;indirizzo non ha ancora un Account Tray viene creato, e la persona riceve il
            link per impostare la password. È così che un cliente solo on-prem — che non ha nessun
            workspace — ottiene un accesso per aprire un ticket.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="om-email">Email</Label>
            <Input
              id="om-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@azienda.it"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="om-name">Nome e cognome (opzionale)</Label>
            <Input id="om-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Solo un nome di partenza, per non avere un indirizzo nudo nell&apos;elenco: è la
              persona a cambiarlo dal proprio Account Tray.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="om-role">Ruolo</Label>
            <select
              id="om-role"
              className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
            >
              <option value="admin">admin — può agire sull&apos;organizzazione</option>
              <option value="member">membro — vi appartiene, senza poteri</option>
            </select>
          </div>
          {save.isError && (
            <p className="text-sm text-danger">
              {save.error instanceof Error ? save.error.message : "Non riuscito"}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button onClick={() => save.mutate()} disabled={!email.trim() || save.isPending}>
            {save.isPending ? "Aggiungo…" : "Aggiungi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The organization's PEOPLE — link-or-INVITE (ADR-0082 D8).
 *
 * An address that already has an Account Tray is linked; one that doesn't gets an account
 * created and a claim mail. This is the door for a customer who runs ONLY on-prem: they have
 * no workspace anywhere, and Central derives their customer hat from THIS arc, so without it
 * the people who pay for a licence could not open a support ticket.
 *
 * The outcome is SHOWN rather than toasted because it carries the claim link: the mail is the
 * convenience, the copyable link is the guarantee.
 */
function MembersSection({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = React.useState(false);
  const key = [...ORGANIZATIONS_KEY, organizationId, "members"];
  const q = useQuery({ queryKey: key, queryFn: () => platformApi.listOrgMembers(organizationId) });
  const write = <T,>(fn: () => Promise<T>) =>
    fn().then((rows) => {
      qc.setQueryData(key, rows);
      return rows;
    });
  const setRoleFor = useMutation({
    mutationFn: (v: { id: string; role: "admin" | "member" }) =>
      write(() => platformApi.setOrgMemberRole(organizationId, v.id, v.role)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => write(() => platformApi.removeOrgMember(organizationId, id)),
  });
  const error = setRoleFor.error || remove.error;
  const rows = q.data ?? [];

  return (
    <Section
      title="Persone"
      icon={Users}
      count={rows.length}
      action={
        <Button size="sm" className="h-7 px-2 text-xs" onClick={() => setAdding(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={2} /> Account
        </Button>
      }
    >
      {q.isLoading ? (
        <Empty>Carico…</Empty>
      ) : rows.length === 0 ? (
        <Empty>
          Nessuna persona collegata. Aggiungi l&apos;amministratore di questa organizzazione.
        </Empty>
      ) : (
        <Table>
          <Thead>
            <Tr>
              <Th>Persona</Th>
              <Th>Email</Th>
              <Th>Ruolo</Th>
              <Th>Account</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((m) => (
              <Tr key={m.identity_id}>
                {/* The email has its OWN column, so the name cell must not fall back to it —
                    the same address twice on one row reads as a rendering bug. Someone
                    invited but not yet claimed simply has no name yet. */}
                <Td className="font-medium">{m.full_name || "—"}</Td>
                <Td className="text-muted-foreground">{m.email}</Td>
                <Td>
                  <Badge tone={m.role === "admin" ? "active" : "neutral"}>{m.role}</Badge>
                </Td>
                <Td>
                  {m.identity_state === "active" ? (
                    <span className="text-muted-foreground">attivo</span>
                  ) : (
                    <Badge tone="warning">{m.identity_state}</Badge>
                  )}
                </Td>
                <Td className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={setRoleFor.isPending}
                      onClick={() =>
                        setRoleFor.mutate({
                          id: m.identity_id,
                          role: m.role === "admin" ? "member" : "admin",
                        })
                      }
                    >
                      {m.role === "admin" ? "Rendi membro" : "Rendi admin"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-danger"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(m.identity_id)}
                    >
                      Rimuovi
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {error instanceof Error && <p className="mt-2 text-sm text-danger">{error.message}</p>}

      {adding && (
        <AddOrgMemberDialog
          organizationId={organizationId}
          onClose={() => setAdding(false)}
          // The mutation's response IS the refreshed roster, so the grid updates without a
          // second fetch — while the dialog stays open to show the claim link.
          onAdded={(res) => qc.setQueryData(key, res.members)}
        />
      )}
    </Section>
  );
}

export function PlatformOrganizationDetailPage() {
  const { organizationId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<PlatformOrganization | null>(null);
  const [moving, setMoving] = React.useState<LinkTarget | null>(null);

  const detailQ = useQuery({
    queryKey: [...ORGANIZATIONS_KEY, organizationId],
    queryFn: () => platformApi.getOrganization(organizationId),
    enabled: !!organizationId,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ORGANIZATIONS_KEY });
  const detail = detailQ.data;

  const archive = useMutation({
    mutationFn: () =>
      platformApi.updateOrganization(organizationId, {
        status: detail?.status === "archived" ? "active" : "archived",
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => platformApi.deleteOrganization(organizationId),
    // The record is gone, so there is nothing to come back to: leave for the list.
    onSuccess: () => {
      invalidate();
      navigate("..");
    },
  });

  const back = (
    <button
      type="button"
      onClick={() => navigate("..")}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
      Organizzazioni
    </button>
  );

  if (detailQ.isLoading) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
          </div>
        </div>
      </PageContainer>
    );
  }
  if (detailQ.isError || !detail) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-start gap-2 text-sm text-danger">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            {detailQ.error instanceof PlatformApiError ? detailQ.error.message : "Errore di rete."}
          </div>
        </div>
      </PageContainer>
    );
  }

  const moveButton = (target: LinkTarget) => (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={() => setMoving(target)}
    >
      Sposta
    </Button>
  );

  return (
    <PageContainer>
      {/* Header band — back-link, identity, live counts, lifecycle actions. Full-width. */}
      <div className="shrink-0 border-b border-border/60 px-4 pt-5 pb-4 sm:px-8 sm:pt-6">
        {back}
        <div className="mt-3 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-surface text-muted-foreground">
              <Building2 className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
                  {detail.name}
                </h1>
                {detail.status === "archived" && <Badge tone="neutral">Archiviata</Badge>}
              </div>
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {detail.legal_name || "Nessuna ragione sociale"}
                {detail.vat_number ? ` · ${detail.vat_number}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <Metric label="Workspace" value={detail.tenants.length} />
            <span className="h-9 w-px bg-border/60" />
            <Metric label="On-prem" value={detail.licenses.length} />
            <span className="h-9 w-px bg-border/60" />
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Modifica"
                onClick={() => setEditing(detail)}
              >
                <Pencil className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={detail.status === "archived" ? "Riattiva" : "Archivia"}
                title="Archiviare non tocca workspace e licenze collegati"
                onClick={() => archive.mutate()}
              >
                <Archive className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Elimina"
                title="Elimina solo la scheda commerciale: workspace e licenze restano attivi, senza organizzazione"
                onClick={() => {
                  if (
                    window.confirm(
                      `Eliminare la scheda di "${detail.name}"? I suoi ${detail.tenants.length} workspace e ${detail.licenses.length} licenze restano attivi, ma senza organizzazione collegata.`,
                    )
                  )
                    remove.mutate();
                }}
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <PageContent className="space-y-8 pt-6">
        {/* ANAGRAFICA FIRST (owner): it is who the company IS. What it HOLDS follows. */}
        <Section
          title="Anagrafica"
          icon={Building2}
          action={
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setEditing(detail)}
            >
              Modifica
            </Button>
          }
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Fact label="Ragione sociale" value={detail.legal_name} />
            <Fact label="P.IVA" value={detail.vat_number} />
            <Fact label="Codice fiscale" value={detail.tax_code} />
            <Fact label="Paese" value={detail.country} />
            <Fact label="Email" value={detail.email} />
            <Fact label="Telefono" value={detail.phone} />
            <Fact label="PEC" value={detail.pec} />
            <Fact label="Codice SDI" value={detail.sdi_code} />
          </div>
          {detail.notes && (
            <p className="mt-4 whitespace-pre-wrap border-t border-border/60 pt-3 text-sm text-muted-foreground">
              {detail.notes}
            </p>
          )}
        </Section>

        {/* Then WHAT IT HOLDS — the join this page exists for. Full-width grids rather than
            two half-width boxes: the on-prem row carries five facts, and squeezing it into a
            column is what made the old layout unreadable. */}
        <Section title="Workspace cloud" icon={Server} count={detail.tenants.length}>
          {detail.tenants.length === 0 ? (
            <Empty>Nessun workspace collegato.</Empty>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Workspace</Th>
                  <Th>Slug</Th>
                  <Th>Stato</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {detail.tenants.map((t) => (
                  <Tr key={t.id}>
                    {/* The name may be unset, the slug never is — so the LINK lives on the
                        slug and the name cell stays honest. Falling back to the slug here
                        would print the same string twice on the row. */}
                    <Td className="font-medium">{t.name || "—"}</Td>
                    <Td>
                      <Link
                        to={`/platform/tenants/${t.id}`}
                        className="font-mono text-xs underline-offset-2 hover:underline"
                      >
                        {t.slug}
                      </Link>
                    </Td>
                    <Td>
                      <Badge tone={t.status === "active" ? "active" : "warning"}>{t.status}</Badge>
                    </Td>
                    <Td className="text-right">
                      {moveButton({ kind: "tenant", id: t.id, label: t.name || t.slug })}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Section>

        <Section title="Installazioni on-prem" icon={KeyRound} count={detail.licenses.length}>
          {detail.licenses.length === 0 ? (
            <Empty>Nessuna licenza collegata.</Empty>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Chiave</Th>
                  <Th>Stato</Th>
                  <Th>Installazione</Th>
                  <Th>Versione</Th>
                  <Th>Riporta</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {detail.licenses.map((l) => (
                  <Tr key={l.id}>
                    <Td>
                      <code className="text-xs">{l.activation_key_prefix}…</code>
                    </Td>
                    <Td>
                      <Badge tone={l.status === "active" ? "active" : "neutral"}>{l.status}</Badge>
                    </Td>
                    <Td className="text-muted-foreground">
                      {l.installation_id ? "attivata" : "mai attivata"}
                    </Td>
                    {/* What the install REPORTS about itself (ADR-0074 L1) — one that never
                        reported says nothing here, it never says zero. */}
                    <Td className="font-mono text-xs text-muted-foreground">
                      {reportVersion(l.last_report) ? `v${reportVersion(l.last_report)}` : "—"}
                    </Td>
                    <Td className="text-muted-foreground">{reportSummary(l.last_report) || "—"}</Td>
                    <Td className="text-right">
                      {moveButton({
                        kind: "license",
                        id: l.id,
                        label: `${l.activation_key_prefix}…`,
                      })}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Section>

        <MembersSection organizationId={organizationId} />
      </PageContent>

      {editing && (
        <OrganizationDialog
          organization={editing}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
      {moving && (
        <OrganizationLinkDialog
          target={moving}
          currentOrganizationId={organizationId}
          onClose={() => setMoving(null)}
        />
      )}
    </PageContainer>
  );
}
